import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Button from '../../../../../../components/global/Button';
import ContributorProfileBadge from '../../../../../../components/global/ContributorProfileBadge';
import MapMarkerIcon from '../../../../../../components/icons/MapMarkerIcon';
import AlbumsIcon from '../../../../../../components/icons/AlbumsIcon';
import CalendarIcon from '../../../../../../components/icons/CalendarIcon';
import LockIcon from '../../../../../../components/icons/LockIcon';
import { getAsset } from '../../../../../../util/assets';
import { getThumbBySize } from '../../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import { truncateText } from '../../../../../../util/text';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import getConfig from '../../../../../../util/config';
import { BASENAME } from '../../../../../../constants/basenames';

const propTypes = {
  album: PropTypes.shape({
    creator: PropTypes.object,
    cover_photo: PropTypes.object,
    id: PropTypes.string,
    start_date: PropTypes.string,
    name: PropTypes.string,
    location: PropTypes.string,
    description: PropTypes.string,
    has_password: PropTypes.bool,
  }).isRequired,
  authenticated: PropTypes.bool,
  onClick: PropTypes.func,
};

const AlbumSearchResult = ({ album, authenticated, onClick }) => {
  const DATE_FORMAT = 'MMM D, Y';

  const { id, creator } = album;
  const { username } = creator;

  const creatorAvatarPhotoSrc = creator?.avatar_photo
    ? getThumbBySize(creator.avatar_photo.thumbnail_urls, IMAGE_SIZE_CODE.SMALL)
        .url
    : undefined;
  const viewLinkTarget = authenticated
    ? `/app/${BASENAME.SHUTTERBUG}/hierarchy-items/${id}`
    : `/galleries/view/album/${id}`;

  // cover photo for gallery, or placeholder graphic.
  const coverPhotoUrl = album.cover_photo
    ? getThumbBySize(album.cover_photo.thumbnail_urls, IMAGE_SIZE_CODE.LARGE)
        .url
    : getAsset('/web-galleries/images/tiles.svg');

  const dateRangeString = (() => {
    return album.start_date
      ? moment(album.start_date).format(DATE_FORMAT)
      : undefined;
  })();
  const hasUploadPermissions =
    !!album.auto_contributor_settings?.upload_access &&
    album.auto_contributor_settings?.upload_access !==
      CONTRIBUTOR_UPLOAD_ACCESS.NONE;
  const uploadRouteUrl = `${getConfig().app.baseUrl}/contributors/u/${
    album?.id
  }`;

  return (
    <div className="wg-search-result wg-search-result-album shadowed-search-result">
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a
        aria-label="Event result"
        className="cover-photo-head"
        href={viewLinkTarget}
        target="_parent"
        style={{ backgroundImage: `url("${coverPhotoUrl}")` }}
        onClick={event => {
          if (onClick) {
            event.preventDefault();
            onClick(event);
          }
        }}
      />

      <div className="result-details">
        <h4 className="result-title mb-1">
          <a
            href={viewLinkTarget}
            target="_parent"
            onClick={event => {
              if (onClick) {
                event.preventDefault();
                onClick(event);
              }
            }}
          >
            <AlbumsIcon color="#1A2E34" /> {album.name}
          </a>
        </h4>

        <ContributorProfileBadge
          className="mb-1"
          displayName={creator?.name}
          displayUsername={username}
          displayPhotoSrc={creatorAvatarPhotoSrc}
          href={username ? `/@${username}` : undefined}
          target="_parent"
          prependText="by"
          characterWidthLimit={30}
        />

        <div className="result-meta mt-1">
          {album.location && (
            <span className="result-meta-item result-meta-item-location">
              <MapMarkerIcon color="#9BA8AC" /> {album.location.name}
            </span>
          )}

          {dateRangeString && (
            <span className="result-meta-item result-meta-item-date">
              <CalendarIcon size={13} color="#9BA8AC" /> {dateRangeString}
            </span>
          )}
        </div>

        {album.description && (
          <div className="result-description">
            {truncateText(album.description, 20)}
          </div>
        )}

        <div className="wg-search-result-footer">
          <Button
            btnStyle="yellow"
            btnSize="sm"
            className="view-result-button"
            href={viewLinkTarget}
            target="_parent"
            onClick={event => {
              if (onClick) {
                event.preventDefault();
                onClick(event);
              }
            }}
          >
            {album.has_password && (
              <LockIcon size={10} color="#000" className="mr-1" />
            )}
            Browse
          </Button>

          {hasUploadPermissions && (
            <Button
              btnSize="sm"
              className="view-result-button"
              href={uploadRouteUrl}
              target="_parent"
            >
              Upload Photos
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

AlbumSearchResult.propTypes = propTypes;

export default AlbumSearchResult;
